import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { REASON_CODES, REASON_LABELS, feedbackLine, parsePreferredPrice } from '../utils/aiFeedback';
import { timeAgo, parseUtcDate } from '../utils/dates';

const MARGIN = 8; // keep this far from every viewport edge

const fmtPrice = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? `$${Number.isInteger(n) ? n : n.toFixed(2)}` : '-';
};

// Rate one AI suggestion: right/wrong, a reason when wrong, and (admins) an
// "I'd use $X" price and a note. Saving records feedback ONLY — nothing goes to
// Skybox. The one exception is the explicit "Save & apply $X" button, which the
// parent wires to the existing accept path (shown only while the suggestion is
// pending or stale and onSaveAndApply is provided).
//
// Stays open until Save, Cancel or Esc (an outside click does not close it, so
// a half-typed rating is never lost). Rendered in a portal with fixed position,
// anchored under (or above) anchorEl and clamped inside the viewport.
//
// Props:
//   suggestion       { id, status, action, suggestedPrice, currentPrice, feedback? }
//   anchorEl         element to anchor to (null → centered)
//   isAdmin          admin-like: price + note fields; otherwise verdict + reasons only
//   mode             'rate' | 'dismiss' (dismiss: prefilled "wrong", Save dismisses too)
//   onSave(payload)                 → Promise; payload { verdict, reasonCode, preferredPrice, note }
//   onSaveAndApply(payload, price)  → Promise (optional)
//   onSkip()                        → Promise (dismiss mode: dismiss without rating)
//   onClose()
function AiRatePopover({ suggestion, anchorEl, isAdmin, mode = 'rate', onSave, onSaveAndApply, onSkip, onClose }) {
  const mine = suggestion?.feedback?.mine || null;
  const dismissMode = mode === 'dismiss';

  const [verdict, setVerdict] = useState(dismissMode ? 'wrong' : (mine?.verdict || null));
  const [reasonCode, setReasonCode] = useState(mine?.reasonCode || null);
  const [priceText, setPriceText] = useState(mine?.preferredPrice != null ? String(mine.preferredPrice) : '');
  const [note, setNote] = useState(mine?.note || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const preferredPrice = isAdmin ? parsePreferredPrice(priceText) : null;
  const priceInvalid = Number.isNaN(preferredPrice);
  const valid = !!verdict && (verdict === 'right' || !!reasonCode) && !priceInvalid;

  const payload = () => ({
    verdict,
    reasonCode: verdict === 'wrong' ? reasonCode : null,
    preferredPrice: preferredPrice ?? null,
    note: isAdmin ? note.trim().slice(0, 300) : '',
  });

  // "Save & apply $X": the typed price, else (verdict right) the AI's own price
  const openStatus = suggestion?.status === 'pending' || suggestion?.status === 'stale';
  const applyPrice = preferredPrice != null && !priceInvalid
    ? preferredPrice
    : (verdict === 'right' && suggestion?.action !== 'hold' ? Number(suggestion?.suggestedPrice) : null);
  const canApply = isAdmin && !dismissMode && openStatus && typeof onSaveAndApply === 'function'
    && Number.isFinite(applyPrice) && applyPrice > 0;

  const run = async (fn) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await fn();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save');
      setBusy(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!valid) return;
    run(() => onSave(payload()));
  };

  // ---- position: fixed, anchored, clamped to the viewport ----
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);
  const place = useCallback(() => {
    const el = popRef.current;
    if (!el) return;
    const vw = window.innerWidth, vh = window.innerHeight;
    const w = el.offsetWidth, h = el.offsetHeight;
    const r = anchorEl && anchorEl.isConnected ? anchorEl.getBoundingClientRect() : null;
    let top, left;
    if (r && (r.width || r.height)) {
      left = r.right - w; // right edges aligned with the anchor
      top = r.bottom + 6;
      if (top + h > vh - MARGIN && r.top - h - 6 >= MARGIN) top = r.top - h - 6; // flip above
    } else {
      // Anchor gone (its row re-rendered) — stay where we were
      setPos(prev => prev || { top: Math.max(MARGIN, (vh - h) / 2), left: Math.max(MARGIN, (vw - w) / 2) });
      return;
    }
    left = Math.min(Math.max(MARGIN, left), Math.max(MARGIN, vw - w - MARGIN));
    top = Math.min(Math.max(MARGIN, top), Math.max(MARGIN, vh - h - MARGIN));
    setPos(prev => (prev && Math.abs(prev.top - top) < 0.5 && Math.abs(prev.left - left) < 0.5 ? prev : { top, left }));
  }, [anchorEl]);

  // Re-measure after every render (the reason chips change the height); the
  // equality bail-out in place() keeps this from looping
  useLayoutEffect(() => { place(); });
  useEffect(() => {
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [place]);

  // Esc closes; focus moves into the popover and back to the anchor afterwards
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onCloseRef.current(); }
    };
    document.addEventListener('keydown', onKey);
    popRef.current?.focus({ preventScroll: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      if (anchorEl && anchorEl.isConnected) anchorEl.focus?.({ preventScroll: true });
    };
  }, [anchorEl]);

  const others = Math.max(0, (suggestion?.feedback?.count ?? 0) - (mine ? 1 : 0));
  const latestLabel = suggestion?.feedback?.latestLabel;
  const labelByOther = latestLabel && (!mine || latestLabel.id !== mine.id) ? latestLabel : null;
  const priceLabel = suggestion?.action === 'hold' ? 'hold' : fmtPrice(suggestion?.suggestedPrice);

  const node = (
    <div
      ref={popRef}
      className="ai-rate-popover"
      role="dialog"
      aria-label={dismissMode ? 'Dismiss AI suggestion' : 'Rate AI suggestion'}
      tabIndex={-1}
      style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      <form onSubmit={handleSubmit}>
        <div className="ai-rate-head">
          <strong>{dismissMode ? 'Dismiss' : 'Rate'} AI suggestion · {priceLabel}</strong>
          {suggestion?.currentPrice != null && <span className="ai-admin-note"> from {fmtPrice(suggestion.currentPrice)}</span>}
          <button type="button" className="modal-close ai-rate-close" onClick={onClose} title="Cancel (Esc)" aria-label="Cancel">×</button>
        </div>

        {mine?.verdict && (
          <div className="ai-admin-note">
            Your rating: {feedbackLine(mine)}{mine.updatedAt ? ` · ${timeAgo(parseUtcDate(mine.updatedAt))}` : ''}
          </div>
        )}
        {labelByOther && (
          <div className="ai-admin-note">Label by {labelByOther.userName || 'an admin'}: {feedbackLine(labelByOther)}</div>
        )}
        {!labelByOther && others > 0 && (
          <div className="ai-admin-note">{others} other rating{others === 1 ? '' : 's'}</div>
        )}

        <div className="ai-rate-verdict" role="group" aria-label="Verdict">
          <button type="button" className={`ai-rate-chip verdict-right${verdict === 'right' ? ' selected' : ''}`} onClick={() => setVerdict('right')}>✓ Right</button>
          <button type="button" className={`ai-rate-chip verdict-wrong${verdict === 'wrong' ? ' selected' : ''}`} onClick={() => setVerdict('wrong')}>✗ Wrong</button>
        </div>

        {verdict === 'wrong' && (
          <div className="ai-rate-reasons" role="group" aria-label="Why is it wrong?">
            {REASON_CODES.map(code => (
              <button
                key={code}
                type="button"
                className={`ai-rate-chip${reasonCode === code ? ' selected' : ''}`}
                onClick={() => setReasonCode(code)}
              >
                {REASON_LABELS[code]}
              </button>
            ))}
          </div>
        )}

        {isAdmin && (
          <>
            <label className="ai-rate-field">
              <span>I'd use $</span>
              <input
                type="text"
                inputMode="decimal"
                value={priceText}
                onChange={e => setPriceText(e.target.value)}
                placeholder="optional"
                className={priceInvalid ? 'has-error' : undefined}
                aria-describedby="ai-rate-price-help"
              />
              <span id="ai-rate-price-help" className="ai-rate-help">not written to Skybox</span>
            </label>
            {priceInvalid && <div className="ai-inline-error">Enter a price above $0 (or leave it blank)</div>}
            <label className="ai-rate-field ai-rate-note">
              <span>Note</span>
              <textarea value={note} onChange={e => setNote(e.target.value)} maxLength={300} rows={2} placeholder="optional" />
            </label>
          </>
        )}

        {error && <div className="ai-inline-error">{error}</div>}

        <div className="ai-rate-actions">
          <button type="submit" className="btn-primary btn-sm" disabled={!valid || busy}>
            {busy ? 'Saving…' : dismissMode ? 'Dismiss & save' : 'Save'}
          </button>
          {canApply && (
            <button
              type="button"
              className="btn-success btn-sm"
              disabled={!valid || busy}
              title={`Saves your rating, then writes B ${fmtPrice(applyPrice)} to Skybox through the normal accept path`}
              onClick={() => run(() => onSaveAndApply(payload(), applyPrice))}
            >
              Save &amp; apply {fmtPrice(applyPrice)}
            </button>
          )}
          {dismissMode && typeof onSkip === 'function' && (
            <button type="button" className="btn-secondary btn-sm" disabled={busy} onClick={() => run(() => onSkip())}>
              Dismiss, no rating
            </button>
          )}
          <button type="button" className="btn-link" onClick={onClose}>{dismissMode ? 'Keep it' : 'Cancel'}</button>
        </div>
      </form>
    </div>
  );

  return createPortal(node, document.body);
}

export default AiRatePopover;
